/**
 * GameEngine.ts — Scene manager for Gallowcrest.
 *
 * Manages a stack of scenes. The topmost scene receives input events
 * and is responsible for rendering. Simplified from Ascend (no turn counter,
 * no AI turn hooks — Gallowcrest is a linear narrative, not a turn-based sim).
 */

import type { Terminal } from '@ui/Terminal';
import type { InputManager } from '@ui/InputManager';
import type { MenuSystem } from '@ui/MenuSystem';
import type { StatusBar } from '@ui/StatusBar';
import type { AsciiArtRenderer } from '@ui/AsciiArt';
import type { TextEffects } from '@ui/TextEffects';
import type { ScreenLayout } from '@ui/ScreenLayout';
import type { ViewportManager } from '@ui/ViewportManager';
import type { InputPrompt } from '@ui/InputPrompt';
import type { NarrativeRenderer } from '@ui/NarrativeRenderer';
import type { StatCheckRenderer } from '@ui/StatCheckRenderer';
import { EventSystem } from './EventSystem';
import type { NameGenerator, SeededRNG } from './RandomGen';
import type { GameState } from './GameState';
import type { StoryEngine } from '@core/systems/StoryEngine';
import type { SkillCheckSystem } from '@core/systems/SkillCheckSystem';
import type { CharacterSystem } from '@core/systems/CharacterSystem';
import type { SaveLoadSystem } from './SaveLoad';
import type { SettingsManager } from '@game/Settings';
import type { AbilitySystem } from '@core/systems/AbilitySystem';
import type { AchievementSystem } from '@core/systems/AchievementSystem';
import type { CRTEffect } from '@ui/CRTEffect';
import type { ParticleSystem } from '@ui/ParticleSystem';
import type { CombatSystem } from '@core/systems/CombatSystem';
import type { SkillSystem } from '@core/systems/SkillSystem';
import type { TravelSystem } from '@core/systems/TravelSystem';
import type { FactionSystem } from '@core/systems/FactionSystem';
import type { VisualLayer } from '@ui/VisualLayer';
import type { TerminalFrame } from '@ui/TerminalFrame';
import type { HintManager } from '@ui/HintManager';
import type { SpriteRenderer } from '@ui/SpriteRenderer';
import type { CanvasPixelRenderer } from '@ui/CanvasPixelRenderer';
import type { FrameRenderer } from '@ui/FrameRenderer';
import type { TransitionManager } from '@ui/TransitionManager';
import type { NotificationManager } from '@ui/AchievementNotifier';
import type { TooltipSystem } from '@ui/TooltipSystem';
import type { ProgressIndicator } from '@ui/ProgressIndicator';
import type { ModalManager } from '@ui/ModalManager';

// --- Scene Interface ---

export interface Scene {
  /** Unique name for debugging */
  name: string;

  /** Called when scene becomes the active (top) scene */
  enter(engine: GameEngine): Promise<void>;

  /** Called when scene is no longer the active scene */
  exit(engine: GameEngine): Promise<void>;
}

// --- Game Context ---

/** All UI and engine services available to scenes */
export interface GameContext {
  terminal: Terminal;
  input: InputManager;
  menu: MenuSystem;
  statusBar: StatusBar;
  art: AsciiArtRenderer;
  effects: TextEffects;
  transitions: TransitionManager;
  layout: ScreenLayout;
  viewport: ViewportManager;
  events: EventSystem;
  prompt: InputPrompt;
  narrative: NarrativeRenderer;
  statCheckRenderer: StatCheckRenderer;
  rng: SeededRNG;
  names: NameGenerator;

  // Phase 3 systems (mutable — gameState set after character creation/load)
  gameState: GameState | null;
  storyEngine: StoryEngine;
  skillCheck: SkillCheckSystem;
  characterSystem: CharacterSystem;
  saveSystem: SaveLoadSystem;
  settings: SettingsManager;
  abilitySystem: AbilitySystem;
  achievementSystem: AchievementSystem;

  // Combat
  combatSystem: CombatSystem;

  // Skill trees
  skillSystem: SkillSystem;

  // World navigation & factions
  travelSystem: TravelSystem;
  factionSystem: FactionSystem;

  // Visual effects
  crt: CRTEffect;
  particles: ParticleSystem;
  visualLayer: VisualLayer;
  terminalFrame: TerminalFrame;

  // UI enhancement modules
  hintManager: HintManager;
  sprites: SpriteRenderer;
  canvasArt: CanvasPixelRenderer;
  frameRenderer: FrameRenderer;
  notifier: NotificationManager;
  tooltips: TooltipSystem;
  modalManager: ModalManager;
  progressIndicator: ProgressIndicator;
}

// --- GameEngine ---

export class GameEngine {
  private sceneStack: Scene[] = [];
  private context: GameContext;
  private running = false;

  constructor(context: GameContext) {
    this.context = context;
  }

  /** Get the game context (UI services, events, etc.) */
  getContext(): GameContext {
    return this.context;
  }

  /** Get the event system */
  get events(): EventSystem {
    return this.context.events;
  }

  // --- Scene Lifecycle Helpers ---

  /** Framework-level cleanup between scene transitions.
   *  Called after exit() and before the new scene's enter(). */
  private cleanupBetweenScenes(): void {
    const ctx = this.context;
    ctx.sprites.hide();
    ctx.frameRenderer.clearFooter();
    ctx.tooltips.clear();
    // Don't call terminal.clear() — the fade transition handles visual clearing
    // Don't stop particles — they're ambient and persist across scenes by design
  }

  /** Auto-save to slot 0 before scene transitions when gameState exists. */
  private autoSaveIfPossible(): void {
    const { gameState, saveSystem } = this.context;
    if (!gameState || !saveSystem) return;
    try {
      const className = gameState.character.classId ?? 'Unknown';
      const chapterName = 'Auto-save';
      saveSystem.save(0, gameState, chapterName, className);
    } catch (err) {
      console.warn('[GameEngine] Auto-save failed:', err);
      // Don't block scene transition on save failure
    }
  }

  // --- Scene Management ---

  /** Get the active (topmost) scene */
  getActiveScene(): Scene | undefined {
    return this.sceneStack[this.sceneStack.length - 1];
  }

  /** Get the full scene stack (for debugging) */
  getSceneStack(): ReadonlyArray<Scene> {
    return this.sceneStack;
  }

  /** Push a new scene onto the stack. */
  async pushScene(scene: Scene): Promise<void> {
    this.autoSaveIfPossible();
    const current = this.getActiveScene();
    if (current) {
      await current.exit(this);
      this.cleanupBetweenScenes();
      await this.context.transitions.fadeTransition();
    }
    this.sceneStack.push(scene);
    try {
      await scene.enter(this);
    } catch (err) {
      console.error(`Error entering scene ${scene.name}:`, err);
      this.showSceneError(scene.name, err);
    }
  }

  /** Pop the top scene off the stack. */
  async popScene(): Promise<void> {
    const popped = this.sceneStack.pop();
    if (popped) {
      await popped.exit(this);
      this.cleanupBetweenScenes();
      await this.context.transitions.fadeTransition();
    }
    const current = this.getActiveScene();
    if (current) {
      try {
        await current.enter(this);
      } catch (err) {
        console.error(`Error entering scene ${current.name}:`, err);
        this.showSceneError(current.name, err);
      }
    }
  }

  /** Replace the top scene with a new one. */
  async replaceScene(scene: Scene): Promise<void> {
    this.autoSaveIfPossible();
    const current = this.sceneStack.pop();
    if (current) {
      await current.exit(this);
      this.cleanupBetweenScenes();
      await this.context.transitions.fadeTransition();
    }
    this.sceneStack.push(scene);
    try {
      await scene.enter(this);
    } catch (err) {
      console.error(`Error entering scene ${scene.name}:`, err);
      this.showSceneError(scene.name, err);
    }
  }

  /** Clear the entire scene stack and start fresh with a new root scene. */
  async resetTo(scene: Scene): Promise<void> {
    await this.context.terminal.fadeOut();
    while (this.sceneStack.length > 0) {
      const popped = this.sceneStack.pop();
      if (popped) {
        await popped.exit(this);
        this.cleanupBetweenScenes();
      }
    }
    this.sceneStack.push(scene);
    // Fire fadeIn before enter — enter() blocks for the scene's lifetime
    this.context.terminal.fadeIn().catch((err: unknown) => {
      console.warn('[GameEngine] fadeIn failed:', err);
    });
    try {
      await scene.enter(this);
    } catch (err) {
      console.error(`Error entering scene ${scene.name}:`, err);
      this.showSceneError(scene.name, err);
    }
  }

  /** Show error on terminal when a scene fails to enter. */
  private showSceneError(sceneName: string, err: unknown): void {
    const msg = err instanceof Error ? err.message : String(err);
    this.context.terminal.clear();
    this.context.terminal.writeLine('', 'red');
    this.context.terminal.writeLine('  ERROR', 'red');
    this.context.terminal.writeLine(`  Scene "${sceneName}" failed to load.`, 'red');
    this.context.terminal.writeLine(`  ${msg}`, 'white');
    this.context.terminal.writeLine('');
    this.context.terminal.writeLine('  Press F5 to reload.', 'yellow');
  }

  // --- Lifecycle ---

  /** Start the game by pushing the initial scene */
  async start(initialScene: Scene): Promise<void> {
    this.running = true;
    this.context.events.emit('gameStart');
    await this.pushScene(initialScene);
  }

  /** Check if the game is running */
  isRunning(): boolean {
    return this.running;
  }

  /** Stop the game */
  stop(): void {
    this.running = false;
    this.context.events.emit('gameStop');
  }
}
